import React, { useMemo, useState } from 'react'
import { Box, Button, Stack, Typography } from '@mui/material'
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material'
import { poolwerxColors } from '../../../theme/colors'
import AlertRow from './AlertRow'
import type { AlertItem, AlertSeverity } from './types'

const SEVERITY_ORDER: Record<AlertSeverity, number> = {
  error: 0,
  warning: 1,
  info: 2,
}

const VISIBLE_LIMIT = 3

interface InspectorAlertStackProps {
  alerts: AlertItem[]
}

const InspectorAlertStack: React.FC<InspectorAlertStackProps> = ({ alerts }) => {
  const [showAll, setShowAll] = useState(false)

  const sorted = useMemo(
    () =>
      [...alerts].sort(
        (a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity],
      ),
    [alerts],
  )

  if (sorted.length === 0) {
    return null
  }

  const overflow = sorted.length - VISIBLE_LIMIT
  const showCollapsedRow = overflow > 0 && !showAll
  const visible = showAll ? sorted : sorted.slice(0, VISIBLE_LIMIT)

  return (
    <Stack spacing={1} sx={{ mt: -0.75, mb: 2 }} className="no-print">
      {visible.map((alert) => (
        <AlertRow key={alert.id} alert={alert} />
      ))}
      {showCollapsedRow && (
        <Button
          onClick={() => setShowAll(true)}
          aria-expanded={false}
          aria-label={`Show ${overflow} more alert${overflow === 1 ? '' : 's'}`}
          endIcon={<ExpandMoreIcon />}
          sx={{
            justifyContent: 'space-between',
            textTransform: 'none',
            py: 0.75,
            px: 1.5,
            borderRadius: '10px',
            border: '1px solid rgba(0,0,0,0.06)',
            borderLeft: `4px solid ${poolwerxColors.teal}`,
            backgroundColor: poolwerxColors.infoSoft,
            color: poolwerxColors.charcoal,
          }}
        >
          <Typography
            component="span"
            sx={{ fontSize: '0.875rem', fontWeight: 600 }}
          >
            + {overflow} more alert{overflow === 1 ? '' : 's'}
          </Typography>
        </Button>
      )}
      {showAll && overflow > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            size="small"
            onClick={() => setShowAll(false)}
            endIcon={<ExpandLessIcon />}
            sx={{
              textTransform: 'none',
              color: 'text.secondary',
              fontSize: '0.8rem',
            }}
          >
            Show fewer
          </Button>
        </Box>
      )}
    </Stack>
  )
}

export default InspectorAlertStack
